// ===================================================================
// DEBRIS ADMS — Module Layout with grouped sub-navigation
// Automated Debris Management System — organized nav sections
// ===================================================================

import { Outlet, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconMap2, IconBuildingFactory2,
  IconUsers, IconTruck, IconTicket, IconBuildingWarehouse,
  IconFileInvoice, IconFileText,
  IconSteeringWheel, IconMapRoute,
  IconUpload, IconTower, IconMapPin, IconPhoto, IconInbox,
  IconDeviceMobile, IconCheck, IconCopy,
} from '@tabler/icons-react';
import { Group, Tooltip, CopyButton, ActionIcon, Button } from '../../components/gab/GabComponents';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import ModuleNav from '../../components/layout/ModuleNav';
import type { NavItem } from '../../types/nav';
import PageHeader from '../../components/ui/PageHeader';
// brand color handled by ModuleNav

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',      path: '/debris',              icon: IconLayoutDashboard, exact: true },
  { label: 'Requests',       path: '/debris/requests',     icon: IconInbox },
  { label: 'Zones',          path: '/debris/zones',        icon: IconMap2 },
  { label: 'Contractors',    path: '/debris/contractors',  icon: IconBuildingFactory2 },
  { label: 'Crews',          path: '/debris/crews',        icon: IconUsers },
  { label: 'Drivers',        path: '/debris/drivers',      icon: IconSteeringWheel },
  { label: 'Trucks',         path: '/debris/trucks',       icon: IconTruck },
  { label: 'Load Tickets',   path: '/debris/tickets',      icon: IconTicket },
  { label: 'Field Pickup',  path: '/debris/pickup',       icon: IconMapPin },
  { label: 'Disposal Sites', path: '/debris/sites',        icon: IconBuildingWarehouse },
  { label: 'Monitors',       path: '/debris/monitors',    icon: IconTower },
  { label: 'Right-of-Entry', path: '/debris/roe',         icon: IconTicket },
  { label: 'Photos',        path: '/debris/photos',       icon: IconPhoto },
  { label: 'Map',            path: '/debris/map',          icon: IconMap2 },
  { label: 'Routes',         path: '/debris/routes',       icon: IconMapRoute, minLevel: 'editor' },
  { label: 'Monitor',        path: '/debris/monitor',      icon: IconTower },
  { label: 'Import',         path: '/debris/import',       icon: IconUpload, minLevel: 'editor' },
  { label: 'Rate Schedule',   path: '/debris/rates',        icon: IconFileInvoice, minLevel: 'editor' },
  { label: 'Invoices',       path: '/debris/invoices',     icon: IconFileInvoice, minLevel: 'editor' },
  { label: 'Driver Resources', path: '/debris/documents',  icon: IconFileText },
];

// Group nav items by section for organized display
const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/debris', '/debris/requests'] },
  { label: 'Setup', paths: ['/debris/zones', '/debris/contractors', '/debris/crews', '/debris/drivers', '/debris/trucks', '/debris/sites', '/debris/monitors', '/debris/roe'] },
  { label: 'Operations', paths: ['/debris/tickets', '/debris/pickup', '/debris/monitor', '/debris/routes'] },
  { label: 'Data', paths: ['/debris/photos', '/debris/map', '/debris/import'] },
  { label: 'Admin', paths: ['/debris/rates', '/debris/invoices', '/debris/documents'] },
];

const portalUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/debrisportal`;

function DebrisPortalButton() {
  return (
    <Group gap={4}>
      <Tooltip label="Open field debris portal" withArrow>
        <Button variant="light" color="var(--color-primary-700)" size="xs"
          leftSection={<IconDeviceMobile size={14} />}
          component="a" href={portalUrl} target="_blank">
          Field Portal
        </Button>
      </Tooltip>
      <CopyButton value={portalUrl}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied!' : 'Copy portal link'} withArrow>
            <ActionIcon variant="light" color={copied ? 'var(--color-success)' : 'var(--color-primary-700)'} size="sm" onClick={copy}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

export default function DebrisLayout() {
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('debris', NAV_ITEMS);

  // Build visible items per group
  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Recover' }, { label: 'Debris Management' }]}
        title="Debris Management"
        subtitle="Track every cubic yard from pickup to disposal with a clean FEMA audit trail"
      />
      <ModuleNav items={visibleItems} groups={groups} extra={<DebrisPortalButton />} />
      <Outlet />
    </div>
  );
}
